import { Temporal } from 'temporal-polyfill';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

type DtDateTimeOptions = {
  /** If provided, the resulting instance is converted into this zone. */
  timezone?: string;
};

/**
 * Dynatrace JS-DateTime utility class.
 * Works with ISO 8601 / RFC 3339 date-time strings with/without timezones.
 * Timezone defaults to user's preferred timezone, or UTC if not available.
 * @internal
 */
export class DtDateTime {
  private dateTime: Temporal.ZonedDateTime;

  constructor(dateTimeString: string, options?: DtDateTimeOptions) {
    const normalized = DtDateTime.normalizeRfc3339(dateTimeString);

    // Determine the default/effective zone for naïve timestamps.
    const defaultZone = options?.timezone || getTimezone() || 'UTC';

    // Try parsing as Instant first (captures provided offset/zone).
    let parsedZdt: Temporal.Instant | null = null;

    try {
      // If the input has offset or zone, this will succeed.
      // Example accepted inputs:
      //  - 2020-01-23T17:04:36Z
      //  - 2020-01-23T17:04:36+03:00
      //  - 2020-01-23T17:04:36-05:30
      //  - 2020-01-23T17:04:36+01:00[Europe/Vienna]
      parsedZdt = Temporal.Instant.from(normalized);
    } catch {
      parsedZdt = null;
    }

    if (parsedZdt) {
      // If caller wants to override, convert to that zone (keep instant).
      this.dateTime = parsedZdt.toZonedDateTimeISO(defaultZone);
      // this.dateTime = options?.timezone
      //   ? parsedZdt.withTimeZone(options?.timezone)
      //   : parsedZdt;
      return;
    }

    // If we got here, there's no zone/offset in the input → parse as PlainDateTime.
    let pdt: Temporal.PlainDateTime;
    try {
      // Accepts 2020-01-23T17:04:36 (no offset/zone).
      pdt = Temporal.PlainDateTime.from(normalized);
    } catch {
      throw new Error(`Invalid datetime string: ${dateTimeString}`);
    }

    // Interpret that wall-clock time in the effective default zone.
    // This creates the ZonedDateTime at that local time with DST awareness.
    this.dateTime = pdt.toZonedDateTime(defaultZone);
  }

  // --- Normalization --------------------------------------------------------
  /**
   * Normalize common RFC3339 variants:
   *  - lowercase 't' → 'T'
   *  - lowercase 'z' → 'Z'
   *  - space between date/time or underscore for Grail support → 'T'
   *
   * We only normalize the first separator between date and time (YYYY-MM-DD <sep> HH:mm…)
   * to avoid mangling valid strings that contain spaces elsewhere (e.g. annotations).
   * See: https://ijmacd.github.io/rfc3339-iso8601/ and https://www.rfc-editor.org/rfc/rfc3339.html#section-5.6
   */
  private static normalizeRfc3339(input: string): string {
    let s = input.trim();

    // Replace the first date-time separator (space or underscore) with 'T'
    // Examples: "2020-01-23 17:04:36" → "2020-01-23T17:04:36"
    //           "2020-01-23_17:04:36" → "2020-01-23T17:04:36"
    s = s.replace(/^\d{4}-\d{2}-\d{2}[ _]/, (m) => m.replace(/[ _]/, 'T'));

    // Allow lowercase 't' exactly at the date-time junction
    s = s.replace(/t/, (match, offset) =>
      offset === s.indexOf('t') ? 'T' : match,
    );

    return s;
  }

  // --- Arithmetic (DST safe via ZonedDateTime) ------------------------------
  addDuration(duration: Temporal.DurationLike): DtDateTime {
    const next = this.dateTime.add(duration);
    return DtDateTime.fromZdt(next);
  }

  addDays(days: number): DtDateTime {
    return this.addDuration({ days });
  }

  addWeeks(weeks: number): DtDateTime {
    return this.addDuration({ weeks });
  }

  // --- Output ---------------------------------------------------------------
  toString(): string {
    // Example: 2020-01-23T15:04:36+01:00[Europe/Vienna]
    return this.dateTime.toString();
  }

  private static fromZdt(zdt: Temporal.ZonedDateTime): DtDateTime {
    const obj = Object.create(DtDateTime.prototype) as DtDateTime;
    obj.dateTime = zdt;
    return obj;
  }
}
